import React, { useRef, useState } from 'react';
import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getProDocumentUploadAccept } from '../../utils/files/proDocumentUploadRules';

export type JustificatifType = 'companyRegistration' | 'identityCard' | 'identityCardBack';

interface JustificatifHelpDialogProps {
	open: boolean;
	type: JustificatifType | null;
	onClose: () => void;
	onFilesChange: (type: JustificatifType, files: FileList) => Promise<boolean>;
	compact?: boolean;
}

const CONTENT: Record<JustificatifType, {
	icon: string;
	images: Array<{ src: string; valid: boolean; labelKey?: string }>;
	criteria: number;
}> = {
	companyRegistration: {
		icon: '/images/justificatifs/icons/bank.png',
		images: [
			{ src: '/images/justificatifs/files/kbisOk.png', valid: true },
			{ src: '/images/justificatifs/files/KbisKo.png', valid: false },
		],
		criteria: 3,
	},
	identityCard: {
		icon: '/images/justificatifs/icons/idCard.png',
		images: [
			{ src: '/images/justificatifs/files/cniRectoOk.png', valid: true, labelKey: 'faq_identity_cni_valid' },
			{ src: '/images/justificatifs/files/cniRectoKo.png', valid: false, labelKey: 'faq_identity_cni_invalid' },
			{ src: '/images/justificatifs/files/passeportOk.png', valid: true, labelKey: 'faq_identity_passport_valid' },
			{ src: '/images/justificatifs/files/passeportKo.png', valid: false, labelKey: 'faq_identity_passport_invalid' },
			{ src: '/images/justificatifs/files/cniRectoVersoOk.png', valid: true, labelKey: 'faq_identity_cni_recto_verso_valid' },
		],
		criteria: 3,
	},
	identityCardBack: {
		icon: '/images/justificatifs/icons/idCard.png',
		images: [
			{ src: '/images/justificatifs/files/cniVersoOk.png', valid: true, labelKey: 'faq_identity_cni_valid' },
			{ src: '/images/justificatifs/files/cniVersoKo.png', valid: false, labelKey: 'faq_identity_cni_invalid' },
		],
		criteria: 3,
	},
};

const JustificatifHelpDialog = ({ open, type, onClose, onFilesChange, compact = false }: JustificatifHelpDialogProps) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	if (!type) return null;

	const content = CONTENT[type];
	const handleFiles = async (files: FileList | null) => {
		if (!files || files.length === 0) return;
		if (await onFilesChange(type, files)) onClose();
	};
	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		void handleFiles(event.dataTransfer.files);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={compact ? 'sm' : 'md'}
			PaperProps={{
				style: {
					maxHeight: 'calc(100vh - 32px)',
					borderRadius: compact ? 10 : 6,
					margin: compact ? 16 : 24,
				},
			}}
		>
			<div style={{ padding: compact ? 14 : 22, color: '#323E48', overflowY: 'auto' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						<img src={content.icon} alt="" style={{ width: compact ? 30 : 38, height: compact ? 30 : 34, objectFit: 'contain' }} />
						<div>
							<div style={{ fontSize: compact ? 15 : 19, fontWeight: 'bold' }}>{t(`thirdPanel:faq_${type}_title`)}</div>
							<div style={{ fontSize: compact ? 10 : 12, color: '#707070' }}>{t(`thirdPanel:faq_${type}_subtitle`)}</div>
						</div>
					</div>
					<button type="button" aria-label={t('thirdPanel:faq_close')} onClick={onClose} style={closeButtonStyle}>
						×
					</button>
				</div>

				<div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 8, margin: '16px 0 12px' }}>
					{Array.from({ length: content.criteria }, (_, index) => (
						<div key={index} style={criterionStyle(compact, type === 'companyRegistration' && index === 0)}>
							<img src={`/images/justificatifs/icons/${['sun', 'crop', 'precision'][index]}.png`} alt="" style={{ width: compact ? 14 : 18, height: compact ? 14 : 18 }} />
							{t(`thirdPanel:faq_${type}_criterion_${index + 1}`)}
						</div>
					))}
				</div>

				<div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
					{content.images.map((image, index) => (
						<div key={image.src} style={{ background: image.valid ? '#B9DCC6' : '#F4A9AA', borderRadius: 5, padding: compact ? 6 : 8, gridColumn: !compact && type === 'identityCard' && index === content.images.length - 1 ? '1 / -1' : undefined }}>
							<div style={{ background: image.valid ? '#B9DCC6' : '#F4A9AA', borderRadius: 4, padding: compact ? 5 : 8, height: type === 'companyRegistration' ? 300 : (compact ? 110 : 130), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<img src={image.src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
							</div>
							<div style={{ color: image.valid ? '#26703E' : '#D52C2C', fontSize: compact ? 9 : 11, fontWeight: 'bold', textAlign: 'center', marginTop: 5 }}>
								{t(`thirdPanel:${image.labelKey ?? `faq_${image.valid ? 'valid' : 'invalid'}`}`)}
							</div>
						</div>
					))}
				</div>

				{type === 'companyRegistration' && (
					<div style={{ background: '#D8EFED', borderLeft: '3px solid #3D7C55', borderRadius: 4, marginTop: 12, padding: compact ? '10px 12px' : '12px 14px', color: '#324048' }}>
						<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
							<img src="/images/info.svg" alt="" style={{ width: compact ? 16 : 18, height: compact ? 16 : 18, flexShrink: 0, marginTop: 1 }} />
							<div>
								<div style={{ fontSize: compact ? 10 : 12, fontWeight: 'bold', marginBottom: 5 }}>{t('thirdPanel:faq_companyRegistration_help_title')}</div>
								<div style={{ fontSize: compact ? 9 : 10, lineHeight: 1.35, marginBottom: 6 }}>{t('thirdPanel:faq_companyRegistration_help_text')}</div>
								<a href="https://monidenum.fr" target="_blank" rel="noreferrer" style={{ color: '#4BAF9E', fontSize: compact ? 9 : 10, textDecoration: 'none' }}>
									{t('thirdPanel:faq_companyRegistration_help_link')} <img src="/images/justificatifs/icons/openUrl.svg" alt="" aria-hidden="true" style={{ width: compact ? 12 : 14, height: compact ? 13 : 14, verticalAlign: 'middle', marginLeft: 3 }} />
								</a>
							</div>
						</div>
					</div>
				)}

				<div
					onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
					onDragLeave={() => setIsDragging(false)}
					onDrop={handleDrop}
					style={{ border: `1px dashed ${isDragging ? '#FF4D00' : '#CFCFCF'}`, background: isDragging ? '#FFF4E0' : '#FFFFFF', marginTop: 12, padding: compact ? 12 : 16, textAlign: 'center', borderRadius: 4 }}
				>
					<img src="/images/justificatifs/icons/upload.svg" alt="" style={{ width: 17, height: 27 }} />
					<div style={{ fontSize: compact ? 9 : 11, color: '#707070', margin: '5px 0 8px' }}>{t('thirdPanel:faq_drop_file')}</div>
					<button type="button" onClick={() => inputRef.current?.click()} style={uploadButtonStyle}>
						{t('thirdPanel:faq_import_file')}
					</button>
					<input ref={inputRef} type="file" multiple accept={getProDocumentUploadAccept(type)} style={{ display: 'none' }} onChange={(event) => { void handleFiles(event.target.files); event.currentTarget.value = ''; }} />
				</div>
			</div>
		</Dialog>
	);
};

const closeButtonStyle: React.CSSProperties = {
		border: 0,
		background: 'transparent',
		color: '#707070',
		fontSize: 28,
		lineHeight: 1,
		cursor: 'pointer',
		padding: 0,
};

const uploadButtonStyle: React.CSSProperties = {
		border: '1px solid #D3D3D3',
		background: '#FFFFFF',
		borderRadius: 4,
		padding: '8px 24px',
		fontSize: 11,
		fontWeight: 'bold',
		cursor: 'pointer',
};

const criterionStyle = (compact: boolean, emphasize: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'center',
	gap: 5,
	background: '#E9E9E9',
	borderRadius: 4,
	padding: compact ? '7px 8px' : '9px 12px',
	width: compact ? '100%' : 'auto',
	boxSizing: 'border-box',
	fontSize: compact ? 9 : 11,
	color: '#555555',
	fontWeight: emphasize ? 'bold' : 'normal',
});

export default JustificatifHelpDialog;
